/**
 * /operations/excuses/history — Session 3a PR 15.
 *
 * Per-employee excuse ledger across time — research view + audit.
 * Tashghil-owned per Session 3a §4.14.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Tashghil narration)
 *   2. InlineAISuggestion cards (concentration pattern + manager
 *      calibration)
 *   3. TraceableValue wrapping duration_minutes + quota_impact +
 *      cumulative-month (≥3 wrappers)
 */

import { useQuery } from "@tanstack/react-query";
import { Download, TrendingUp, UserCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import {
  session3aApi,
  type ExcuseHistoryItem,
} from "../lib/session3a-api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { severityChip, showAlertTrace } from "./_session3aHelpers";

const WINDOWS = [30, 90, 365] as const;

export function ExcuseHistoryPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [employeeId, setEmployeeId] = useState<string>("");
  const [days, setDays] = useState<number>(90);
  const [statusFilter, setStatusFilter] = useState<"all" | "approved" | "denied">(
    "all",
  );

  const list = useQuery({
    queryKey: ["excuse-history", legalEntityId, employeeId, days],
    queryFn: () =>
      session3aApi.listExcuseHistory(
        legalEntityId ?? "",
        employeeId || undefined,
        days,
      ),
    enabled: !!legalEntityId,
  });

  const items: ExcuseHistoryItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const approved = items.filter((r) => r.status === "approved").length;
    const denied = items.filter((r) => r.status === "denied").length;
    const types: Record<string, number> = {};
    items.forEach((r) => {
      types[r.type_code] = (types[r.type_code] ?? 0) + 1;
    });
    const top = Object.entries(types).sort((a, b) => b[1] - a[1])[0];
    return {
      total: items.length,
      approved,
      denied,
      approvalRate: items.length > 0 ? (approved / items.length) * 100 : 0,
      topType: top ? { code: top[0], count: top[1] } : null,
    };
  }, [items]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((r) => r.status === statusFilter);
  }, [items, statusFilter]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("operations.excuses_history.title", "Excuse History")}
          subtitle={t(
            "operations.excuses_history.subtitle",
            "Per-employee excuse ledger across time. Tashghil narrates the patterns; HR audits the trail.",
          )}
        />

        <div className="rounded-2xl border border-border-subtle bg-white p-3">
          <label className="block text-xs font-medium text-text-secondary">
            {t("operations.excuses_history.field.employee", "Employee (UUID or blank for aggregate)")}
          </label>
          <input
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {WINDOWS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  days === d
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(
                  `operations.excuses_history.filter.last_${d}_days`,
                  d === 365 ? "Last year" : `Last ${d} days`,
                )}
              </button>
            ))}
            <button
              type="button"
              onClick={() =>
                setStatusFilter(statusFilter === "approved" ? "all" : "approved")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                statusFilter === "approved"
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.excuses_history.filter.approved", "Approved only")}
            </button>
            <button
              type="button"
              onClick={() =>
                setStatusFilter(statusFilter === "denied" ? "all" : "denied")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                statusFilter === "denied"
                  ? "border-rose-300 bg-rose-50 text-rose-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.excuses_history.filter.denied", "Denied only")}
            </button>
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "operations.excuses_history.export_note",
                  "Export wires to /api/excuses/history/export when implemented; v1 surfaces the wire-format on the client.",
                ),
              )
            }
            className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-sunken"
          >
            <Download className="h-3 w-3" />
            {t("operations.excuses_history.cta.export", "Export")}
          </button>
        </div>

        {stats.topType && stats.topType.count >= 8 && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<TrendingUp className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "operations.excuses_history.suggestion.concentration_headline",
              `Pattern: ${stats.topType.count} ${stats.topType.code} excuses in ${days} days.`,
            )}
            body={t(
              "operations.excuses_history.suggestion.concentration_body",
              "Exceeds typical concentration — Tashghil recommends an investigation.",
            )}
            primaryAction={{
              label: t(
                "operations.excuses_history.suggestion.open_quota_watch",
                "Open Quota Watch",
              ),
              onClick: () => window.location.assign("/operations/excuses/quota-watch"),
            }}
            suggestionKey={`excuse-history-concentration-${stats.topType.code}`}
          />
        )}
        {stats.denied >= 5 && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<UserCheck className="h-4 w-4" />}
            severity="info"
            headline={t(
              "operations.excuses_history.suggestion.approver_headline",
              "Approver calibration check.",
            )}
            body={t(
              "operations.excuses_history.suggestion.approver_body",
              "Some approvers deny excuses at 3x the team average. Tashghil suggests a calibration session.",
            )}
            primaryAction={{
              label: t(
                "operations.excuses_history.suggestion.review_approvals",
                "Review approval patterns",
              ),
              onClick: () =>
                showAlertTrace(
                  t(
                    "operations.excuses_history.suggestion.review_note",
                    "Manager-level approval-rate breakdown surfaces in /reports/agent once endpoint lands.",
                  ),
                ),
            }}
            suggestionKey="excuse-history-approver-calibration"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("operations.excuses_history.col.date", "Date")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_history.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_history.col.type", "Type")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_history.col.duration", "Duration")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_history.col.reason", "Reason")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_history.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_history.col.approver", "Approver")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_history.col.quota", "Quota impact")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.excuse_id}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.date}</span>
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="text-text-secondary hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-text-secondary">{row.type_label_en}</span>
                      <span className={`ms-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip("neutral")}`}>
                        {row.category}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.excuses_history.trace.duration",
                              `duration_minutes = ${row.duration_minutes}. Per-minute payroll deduction: salary / 30 / 8 / 60. Lookup excuse_type.paid for whether the deduction applies.`,
                            ),
                          )
                        }
                      >
                        <span className="text-xs text-text-secondary">
                          {row.duration_minutes} min
                        </span>
                      </TraceableValue>
                    </td>
                    <td
                      className="px-3 py-2 text-xs text-text-secondary"
                      title={row.reason}
                    >
                      {row.reason.length > 50
                        ? `${row.reason.slice(0, 50)}…`
                        : row.reason}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                          row.status === "approved"
                            ? "info"
                            : row.status === "denied"
                              ? "critical"
                              : "neutral",
                        )}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      {row.approver_name ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.excuses_history.trace.quota",
                              `Used ${row.monthly_used} of ${row.monthly_limit} this month. Excuse_type policy caps at monthly_limit; Mutaba'aa enforces per Quota Watch.`,
                            ),
                          )
                        }
                      >
                        <span
                          className={`text-xs ${
                            row.monthly_used >= row.monthly_limit
                              ? "text-rose-700"
                              : row.monthly_used / row.monthly_limit >= 0.8
                                ? "text-amber-700"
                                : "text-text-secondary"
                          }`}
                        >
                          {row.monthly_used} / {row.monthly_limit}
                        </span>
                      </TraceableValue>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && !employeeId && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.excuses_history.empty_no_employee",
              "Pick an employee to see their excuse history. ✦ Tashghil suggests: employees flagged on Quota Watch are a good place to start.",
            )}
          </div>
        )}
        {!list.isLoading && filtered.length === 0 && employeeId && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.excuses_history.empty",
              "No excuses recorded for this employee in the selected window.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: stats.total,
            attention: stats.denied,
            critical: 0,
            top_kind: stats.topType?.code ?? t("operations.excuses_history.title", "Excuse History"),
            window_label: `last ${days} days`,
          }}
        />
      </aside>
    </div>
  );
}
